import React, { ReactNode } from 'react';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { ButtonLink } from '@daedalus/atlas/ButtonLink';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import ContentWrapper from '@daedalus/atlas/helpers/ContentWrapper';
import { Flex } from '@daedalus/atlas/helpers/Flex';
import { NoStyleLink } from '@daedalus/atlas/helpers/NoStyleLink';
import { Icon } from '@daedalus/atlas/Icon';
import { Text } from '@daedalus/atlas/Text';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { getRoomAreaWithUnits } from '@daedalus/core/src/room/business/utils/roomAreaUnit';
import { RankedTopRoomAmenities } from '../RoomAmenities';
import { getTopRoomAmenities } from '../RoomAmenities/RoomAmenities';
import type { Room } from '@daedalus/core/src/room/types/room';
type RoomInfoPropsType = {
  room: Pick<Room, 'name' | 'occupationPerRoom' | 'bedTypes' | 'description' | 'amenities' | 'area'>;
  onOpenRoomDetailsOverlay?: () => void;
  isTruncatedLongName?: boolean;
  isDetailsLinkVisible?: boolean;
  hideAmenities?: boolean;
  userCountry: string;
};
const _exp = /*#__PURE__*/() => Flex.Row;
const TitleWrapper = /*#__PURE__*/styled(_exp())({
  name: "TitleWrapper",
  class: "tcuiwlj",
  propsAsIs: true
});
const _exp5 = /*#__PURE__*/() => Text;
const RoomInformationItemWrapper = /*#__PURE__*/styled(_exp5())({
  name: "RoomInformationItemWrapper",
  class: "r1an447m",
  propsAsIs: true
});
const Dot = /*#__PURE__*/styled('div')({
  name: "Dot",
  class: "dv4pcoe",
  propsAsIs: false
});
const _exp7 = /*#__PURE__*/() => Text;
const RoomName = /*#__PURE__*/styled(_exp7())({
  name: "RoomName",
  class: "r141k5pu",
  propsAsIs: true
});
const _exp9 = /*#__PURE__*/() => Icon;
const StyledIcon = /*#__PURE__*/styled(_exp9())({
  name: "StyledIcon",
  class: "scrh150",
  propsAsIs: true
});
const AmenitiesWrapper = /*#__PURE__*/styled('div')({
  name: "AmenitiesWrapper",
  class: "a1qncjtg",
  propsAsIs: false
});
export const createDotSeparatedList = (listItems: (string | ReactNode)[]) => {
  return listItems.map((item, index) => <React.Fragment key={index}>
      {!!index && <Dot />}
      {item}
    </React.Fragment>);
};
const RoomInformationItems = ({
  room,
  userCountry,
  hideAmenities
}: Pick<RoomInfoPropsType, 'room' | 'userCountry' | 'hideAmenities'>) => {
  const informationToDisplay = [...room.bedTypes.filter(bed => bed.name).map(bed => bed.name.trim().split(', ').join(' + '))];
  const roomArea = getRoomAreaWithUnits(room.area, userCountry);
  const showRoomInfo = informationToDisplay.length > 0;
  return <Flex.Column gap="s200" alignItems="flex-start">
      {showRoomInfo && <RoomInformationItemWrapper variant="bodyS" colorPath="content.neutral.c600" dataId="roomInfo.bedType">
          <StyledIcon name="DoubleBed" size="sm" />
          {createDotSeparatedList(informationToDisplay)}
        </RoomInformationItemWrapper>}
      {(roomArea || !hideAmenities && !!getTopRoomAmenities(room.amenities).length) && <AmenitiesWrapper>
          {roomArea && <RoomInformationItemWrapper variant="bodyS" colorPath="content.neutral.c600" dataId="roomInfo.area">
              <StyledIcon name="Maximize2" size="sm" colorPath="content.neutral.c600" />
              {roomArea}
            </RoomInformationItemWrapper>}
          {!hideAmenities && <RankedTopRoomAmenities amenities={room.amenities} />}
        </AmenitiesWrapper>}
    </Flex.Column>;
};
export const RoomInfo = ({
  room,
  onOpenRoomDetailsOverlay,
  isTruncatedLongName = false,
  isDetailsLinkVisible = false,
  hideAmenities = false,
  userCountry
}: RoomInfoPropsType) => {
  const {
    isMobile
  } = useDeviceLayout();
  return <NoStyleLink onClick={isDetailsLinkVisible ? onOpenRoomDetailsOverlay : undefined}>
      <TitleWrapper justifyContent="space-between" alignItems="flex-start" gap="s300">
        <RoomName variant="titleL" colorPath="content.neutral.c950" className={cx(isTruncatedLongName && 'isTruncatedLongName')} dataId="roomInfo.roomName">
          {room.name}
        </RoomName>
        {isDetailsLinkVisible && isMobile && <ButtonLink variant="default" target="_blank" rel="noreferrer" size="lg" isCompact>
            <Text variant="labelS">
              <FormattedMessageWrapper id="details" defaultMessage="Details" />
            </Text>
          </ButtonLink>}
      </TitleWrapper>
      <RoomInformationItems room={room} userCountry={userCountry} hideAmenities={hideAmenities} />
      {isDetailsLinkVisible && !isMobile && <ContentWrapper paddingTop="s300">
          <ButtonLink variant="default" target="_blank" rel="noreferrer" size="lg" isCompact>
            <Text variant="labelS">
              <FormattedMessageWrapper id="roomDetails" defaultMessage="Room details" />
            </Text>
          </ButtonLink>
        </ContentWrapper>}
    </NoStyleLink>;
};

require("./RoomInfo.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./RoomInfo.tsx");